import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

/**
 * Standardized Toast Component
 * Displays notifications with consistent styling across the app
 * 
 * Usage in any component:
 * const toast = useToast();
 * <ToastContainer toasts={toast.toasts} onRemove={toast.remove} />
 */
export const ToastContainer = ({ toasts = [], onRemove = () => {} }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <AlertCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getStyles = (type) => {
    const baseClasses = "flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg backdrop-blur-sm border";
    
    switch (type) {
      case 'success':
        return {
          container: `${baseClasses} bg-green-50 border-green-200 text-green-800`,
          icon: 'text-green-600',
          close: 'hover:bg-green-100 text-green-600',
        };
      case 'error':
        return {
          container: `${baseClasses} bg-red-50 border-red-200 text-red-800`,
          icon: 'text-red-600',
          close: 'hover:bg-red-100 text-red-600',
        };
      case 'warning':
        return {
          container: `${baseClasses} bg-yellow-50 border-yellow-200 text-yellow-800`,
          icon: 'text-yellow-600',
          close: 'hover:bg-yellow-100 text-yellow-600',
        };
      case 'info':
      default:
        return {
          container: `${baseClasses} bg-blue-50 border-blue-200 text-blue-800`,
          icon: 'text-blue-600',
          close: 'hover:bg-blue-100 text-blue-600',
        };
    }
  };

  return (
    <AnimatePresence mode="popLayout">
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm pointer-events-none">
        {toasts.map((toast) => {
          const styles = getStyles(toast.type);
          
          return (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, x: 400, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 400, scale: 0.9 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
              }}
              className={`${styles.container} pointer-events-auto max-w-xs`}
            >
              <div className={styles.icon}>
                {getIcon(toast.type)}
              </div>

              <div className="flex-1">
                <p className="text-sm font-medium break-words">
                  {toast.message}
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onRemove(toast.id)}
                className={`ml-2 p-1 rounded transition-colors ${styles.close}`}
                aria-label="Close notification"
              >
                <X className="w-4 h-4" />
              </motion.button>

              {/* Progress bar for auto-dismiss */}
              {toast.duration > 0 && (
                <motion.div
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: toast.duration / 1000, ease: 'linear' }}
                  className={`absolute bottom-0 left-0 right-0 h-1 origin-left rounded-b-lg ${
                    toast.type === 'success'
                      ? 'bg-green-600'
                      : toast.type === 'error'
                      ? 'bg-red-600'
                      : toast.type === 'warning'
                      ? 'bg-yellow-600'
                      : 'bg-blue-600'
                  }`}
                />
              )}
            </motion.div>
          );
        })}
      </div>
    </AnimatePresence>
  );
};

export default ToastContainer;
